import React, { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  Search,
  Calendar,
  MailIcon,
  CheckCircle,
  AlertCircle,
  Clock,
  Settings,
  Shield,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DateRange } from "react-day-picker";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Progress } from "@/components/ui/progress";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import { ImplementationEmployer, OnboardingStepType, OnboardingStepOverride } from "@/types/operations";

interface OverrideModalProps {
  employer: ImplementationEmployer;
  isOpen: boolean;
  onClose: () => void;
  onOverrideCreated: () => void;
}

const OverrideModal: React.FC<OverrideModalProps> = ({
  employer,
  isOpen,
  onClose,
  onOverrideCreated,
}) => {
  const [stepTypes, setStepTypes] = useState<OnboardingStepType[]>([]);
  const [selectedStepType, setSelectedStepType] = useState<string>("");
  const [overrideReason, setOverrideReason] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingStepTypes, setIsLoadingStepTypes] = useState(false);
  const [localEmployer, setLocalEmployer] = useState<ImplementationEmployer>(employer);

  useEffect(() => {
    if (isOpen) {
      fetchStepTypes();
      setLocalEmployer(employer);
    }
  }, [isOpen, employer]);

  const fetchStepTypes = async () => {
    try {
      setIsLoadingStepTypes(true);
      const types = await OperationsDashboardService.getAvailableOverrideStepTypes();
      setStepTypes(types);
    } catch (error) {
      console.error("Error fetching step types:", error);
    } finally {
      setIsLoadingStepTypes(false);
    }
  };

  const handleCreateOverride = async () => {
    if (!selectedStepType || !overrideReason.trim()) {
      return;
    }

    try {
      setIsLoading(true);
      await OperationsDashboardService.createOnboardingOverride(employer.employer_id, {
        step_type: selectedStepType,
        is_overridden: true,
        override_reason: overrideReason.trim(),
      });
      
      onOverrideCreated();
      onClose();
      setSelectedStepType("");
      setOverrideReason("");
    } catch (error) {
      console.error("Error creating override:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveOverride = async (stepType: string) => {
    try {
      setIsLoading(true);
      await OperationsDashboardService.removeOnboardingOverride(employer.employer_id, stepType);
      
      // Update the local employer state to reflect the removed override
      const updatedEmployer = {
        ...localEmployer,
        overrides: Object.fromEntries(
          Object.entries(localEmployer.overrides).filter(([key]) => key !== stepType)
        )
      };
      setLocalEmployer(updatedEmployer);
      
      // Update the employer prop by calling the parent refresh
      onOverrideCreated();
    } catch (error) {
      console.error("Error removing override:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStepDisplayName = (stepType: string) => {
    const stepTypeMap: Record<string, string> = {
      PARENT_INFO: "Parent Information",
      EMPLOYER_SETUP: "Employer Setup", 
      EMPLOYEE_SETUP: "Employee Setup",
      PENSION_SETUP: "Pension Setup",
      EARNINGS_SETUP: "Earnings Setup",
    };
    return stepTypeMap[stepType] || stepType;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center text-lg">
            <Shield className="w-5 h-5 mr-2" />
            Manage Overrides for {employer.email}
          </DialogTitle>
          <DialogDescription>
            Create or remove onboarding step overrides to unblock this employer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Existing Overrides */}
          <div>
            <h4 className="text-sm font-medium mb-3">Active Overrides</h4>
            {Object.keys(localEmployer.overrides).length > 0 ? (
              <div className="space-y-2">
                {Object.entries(localEmployer.overrides).map(([stepType, override]) => (
                  <div key={stepType} className="flex items-center justify-between p-3 bg-yellow-50 rounded-md border">
                    <div>
                      <div className="font-medium text-sm">{getStepDisplayName(stepType)}</div>
                      <div className="text-xs text-muted-foreground">
                        Overridden by {override.overridden_by_email} on {format(new Date(override.overridden_at), "MMM d, yyyy")}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Reason: {override.override_reason}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveOverride(stepType)}
                      disabled={isLoading}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No active overrides</p>
            )}
          </div>

          {/* Create New Override */}
          <div>
            <h4 className="text-sm font-medium mb-3">Create New Override</h4>
            <div className="space-y-4">
              <div>
                <Label htmlFor="stepType">Step Type</Label>
                <Select value={selectedStepType} onValueChange={setSelectedStepType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a step to override" />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingStepTypes ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : (
                      stepTypes
                        .filter(type => !localEmployer.overrides[type.step_type]) // Don't show already overridden steps
                        .map((type) => (
                          <SelectItem key={type.step_type} value={type.step_type}>
                            {type.display_name}
                          </SelectItem>
                        ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="reason">Override Reason</Label>
                <Textarea
                  id="reason"
                  placeholder="Explain why this override is necessary..."
                  value={overrideReason}
                  onChange={(e) => setOverrideReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateOverride}
            disabled={!selectedStepType || !overrideReason.trim() || isLoading}
          >
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ImplementationEmployersList: React.FC = () => {
  const [employers, setEmployers] = useState<ImplementationEmployer[]>([]);
  const [filteredEmployers, setFilteredEmployers] = useState<
    ImplementationEmployer[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [selectedEmployer, setSelectedEmployer] = useState<ImplementationEmployer | null>(null);
  const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);

  useEffect(() => {
    const fetchEmployers = async () => {
      try {
        setIsLoading(true);
        const data =
          await OperationsDashboardService.getImplementationEmployers();
        setEmployers(data);
        setFilteredEmployers(data);
        setError(null);
      } catch (err) {
        setError("Failed to load implementation employers. Please try again.");
        console.error("Error fetching implementation employers:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployers();
  }, []);

  useEffect(() => {
    // Apply filters when searchQuery or dateRange changes
    let filtered = [...employers];

    // Filter out demo accounts (emails containing "demo-employer")
    filtered = filtered.filter(employer => 
      !employer.email.includes("demo-employer")
    );
    
    // Filter out accounts created before May 20th, 2025
    const cutoffDate = new Date('2025-05-20');
    filtered = filtered.filter(employer => {
      if (!employer.subscription_start_date) return true; // Keep accounts with no subscription start date
      const startDate = new Date(employer.subscription_start_date);
      return startDate >= cutoffDate;
    });

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((employer) =>
        employer.email.toLowerCase().includes(query),
      );
    }

    // Apply date filter
    if (dateRange?.from) {
      filtered = filtered.filter((employer) => {
        if (!employer.subscription_start_date) return false;
        const startDate = new Date(employer.subscription_start_date);
        return startDate >= dateRange.from!;
      });

      if (dateRange.to) {
        filtered = filtered.filter((employer) => {
          if (!employer.subscription_start_date) return false;
          const startDate = new Date(employer.subscription_start_date);
          return startDate <= dateRange.to!;
        });
      }
    }

    setFilteredEmployers(filtered);
  }, [searchQuery, dateRange, employers]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return format(new Date(dateString), "MMM d, yyyy");
  };

  const getSetupStatusIcon = (isComplete: boolean, hasOverride: boolean = false) => {
    if (hasOverride) {
      return (
        <span title="Overridden">
          <Shield className="text-yellow-500 w-4 h-4" />
        </span>
      );
    }
    return isComplete ? (
      <CheckCircle className="text-green-500 w-4 h-4" />
    ) : (
      <AlertCircle className="text-red-500 w-4 h-4" />
    );
  };

  const getProgressColor = (progress: number) => {
    if (progress < 30) return "bg-red-500";
    if (progress < 70) return "bg-yellow-500";
    return "bg-green-500";
  };

  const handleOpenOverrideModal = (employer: ImplementationEmployer) => {
    setSelectedEmployer(employer);
    setIsOverrideModalOpen(true);
  };

  const handleCloseOverrideModal = () => {
    setSelectedEmployer(null);
    setIsOverrideModalOpen(false);
  };

  const handleOverrideCreated = async () => {
    // Refresh the employers list
    try {
      const data = await OperationsDashboardService.getImplementationEmployers();
      setEmployers(data);
    } catch (error) {
      console.error("Error refreshing employers:", error);
    }
  };

  const sortedEmployers = useMemo(() => {
    // Sort by days in implementation (descending) then by progress (ascending)
    return [...filteredEmployers].sort((a, b) => {
      // First by days in implementation (longest first)
      const daysA = a.days_in_implementation || 0;
      const daysB = b.days_in_implementation || 0;
      if (daysB !== daysA) return daysB - daysA;

      // Then by setup progress (least complete first)
      return a.setup_progress - b.setup_progress;
    });
  }, [filteredEmployers]);

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6 flex justify-center items-center h-40">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="ml-2">Loading implementation employers...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardContent className="pt-6">
          <div className="bg-red-50 p-4 rounded-md">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-xl flex items-center">
          <Clock className="w-5 h-5 mr-2" />
          Implementation Employers
        </CardTitle>
        <CardDescription>
          Employers who are still setting up their accounts
        </CardDescription>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between mb-4">
          <div className="relative w-full max-w-sm">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search by name or email..."
              className="pl-8 w-full"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="ml-2">
                <Calendar className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "LLL dd, y")} -{" "}
                      {format(dateRange.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(dateRange.from, "LLL dd, y")
                  )
                ) : (
                  <span>Date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>Days</TableHead>
                <TableHead className="text-center">Parent Info</TableHead>
                <TableHead className="text-center">Employer Setup</TableHead>
                <TableHead className="text-center">Employee</TableHead>
                <TableHead className="text-center">Pension</TableHead>
                <TableHead className="text-center">Earnings</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedEmployers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} className="h-24 text-center">
                    No implementation employers found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedEmployers.map((employer) => (
                  <TableRow key={employer.employer_id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center">
                        <MailIcon className="w-4 h-4 mr-2 text-muted-foreground" />
                        {employer.email}
                        {Object.keys(employer.overrides).length > 0 && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {Object.keys(employer.overrides).length} override{Object.keys(employer.overrides).length !== 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {formatDate(employer.subscription_start_date)}
                    </TableCell>
                    <TableCell>
                      {employer.days_in_implementation ? (
                        <Badge
                          variant={
                            employer.days_in_implementation > 14
                              ? "destructive"
                              : "outline"
                          }
                        >
                          {employer.days_in_implementation} days
                        </Badge>
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {getSetupStatusIcon(employer.has_parent_info, !!employer.overrides.PARENT_INFO)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getSetupStatusIcon(employer.has_employer_setup, !!employer.overrides.EMPLOYER_SETUP)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getSetupStatusIcon(employer.has_employee, !!employer.overrides.EMPLOYEE_SETUP)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getSetupStatusIcon(employer.has_pension_setup, !!employer.overrides.PENSION_SETUP)}
                    </TableCell>
                    <TableCell className="text-center">
                      {getSetupStatusIcon(employer.has_earnings_setup, !!employer.overrides.EARNINGS_SETUP)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress
                          value={employer.setup_progress}
                          className={`h-2 w-16 ${getProgressColor(employer.setup_progress)}`}
                        />
                        <span className="text-xs">
                          {employer.setup_progress}%
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenOverrideModal(employer)}
                      >
                        <Settings className="w-4 h-4 mr-1" />
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>

      <CardFooter className="flex justify-between">
        <div className="text-sm text-muted-foreground">
          Showing {filteredEmployers.length} of {employers.length} employers
        </div>
      </CardFooter>

      {selectedEmployer && (
        <OverrideModal
          employer={selectedEmployer}
          isOpen={isOverrideModalOpen}
          onClose={handleCloseOverrideModal}
          onOverrideCreated={handleOverrideCreated}
        />
      )}
    </Card>
  );
};
